import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ResolutionDocumentation = ({ incident, onUpdateResolution, canEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [resolutionData, setResolutionData] = useState({
    summary: incident?.resolution?.summary || '',
    rootCause: incident?.resolution?.rootCause || '',
    solution: incident?.resolution?.solution || '',
    preventiveMeasures: incident?.resolution?.preventiveMeasures || '',
    lessonsLearned: incident?.resolution?.lessonsLearned || '',
    followUpActions: incident?.resolution?.followUpActions || [],
    verificationSteps: incident?.resolution?.verificationSteps || '',
    timeToResolve: incident?.resolution?.timeToResolve || '',
    resourcesUsed: incident?.resolution?.resourcesUsed || []
  });

  const [newFollowUpAction, setNewFollowUpAction] = useState('');
  const [newResource, setNewResource] = useState('');

  const handleSave = () => {
    onUpdateResolution({
      ...incident,
      resolution: {
        ...resolutionData,
        resolvedBy: incident?.resolution?.resolvedBy || 'Current User',
        resolvedAt: incident?.resolution?.resolvedAt || new Date(),
        lastUpdated: new Date()
      }
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setResolutionData({
      summary: incident?.resolution?.summary || '',
      rootCause: incident?.resolution?.rootCause || '',
      solution: incident?.resolution?.solution || '',
      preventiveMeasures: incident?.resolution?.preventiveMeasures || '',
      lessonsLearned: incident?.resolution?.lessonsLearned || '',
      followUpActions: incident?.resolution?.followUpActions || [],
      verificationSteps: incident?.resolution?.verificationSteps || '',
      timeToResolve: incident?.resolution?.timeToResolve || '',
      resourcesUsed: incident?.resolution?.resourcesUsed || []
    });
    setIsEditing(false);
  };

  const addFollowUpAction = () => {
    if (newFollowUpAction?.trim()) {
      setResolutionData(prev => ({
        ...prev,
        followUpActions: [...prev?.followUpActions, {
          id: Date.now(),
          action: newFollowUpAction?.trim(),
          status: 'pending',
          assignedTo: '',
          dueDate: ''
        }]
      }));
      setNewFollowUpAction('');
    }
  };

  const removeFollowUpAction = (id) => {
    setResolutionData(prev => ({
      ...prev,
      followUpActions: prev?.followUpActions?.filter(action => action?.id !== id)
    }));
  };

  const addResource = () => {
    if (newResource?.trim()) {
      setResolutionData(prev => ({
        ...prev,
        resourcesUsed: [...prev?.resourcesUsed, newResource?.trim()]
      }));
      setNewResource('');
    }
  };

  const removeResource = (index) => {
    setResolutionData(prev => ({
      ...prev,
      resourcesUsed: prev?.resourcesUsed?.filter((_, i) => i !== index)
    }));
  };

  const isResolved = incident?.status?.toLowerCase() === 'resolved' || incident?.status?.toLowerCase() === 'closed';

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="CheckCircle" size={20} />
          Resolution Documentation
        </h2>
        {canEdit && !isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={() => setIsEditing(true)}
          >
            Edit Resolution
          </Button>
        )}
      </div>
      {!isResolved && !isEditing && (
        <div className="text-center py-8 bg-muted/50 rounded-lg">
          <Icon name="Clock" size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-4">
            Resolution documentation will be available once the incident is resolved
          </p>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              onClick={() => setIsEditing(true)}
            >
              Start Documentation
            </Button>
          )}
        </div>
      )}
      {(isResolved || isEditing) && (
        <div className="space-y-6">
          {isEditing ? (
            <>
              {/* Resolution Summary */}
              <div>
                <Input
                  label="Resolution Summary"
                  type="text"
                  value={resolutionData?.summary}
                  onChange={(e) => setResolutionData(prev => ({ ...prev, summary: e?.target?.value }))}
                  placeholder="Brief summary of how the incident was resolved"
                  required
                />
              </div>

              {/* Root Cause */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Root Cause Analysis
                </label>
                <textarea
                  value={resolutionData?.rootCause}
                  onChange={(e) => setResolutionData(prev => ({ ...prev, rootCause: e?.target?.value }))}
                  placeholder="Detailed analysis of what caused the incident"
                  className="w-full h-24 p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
              </div>

              {/* Solution */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Solution Implemented
                </label>
                <textarea
                  value={resolutionData?.solution}
                  onChange={(e) => setResolutionData(prev => ({ ...prev, solution: e?.target?.value }))}
                  placeholder="Detailed description of the solution that was implemented"
                  className="w-full h-24 p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
              </div>

              {/* Preventive Measures */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Preventive Measures
                </label>
                <textarea
                  value={resolutionData?.preventiveMeasures}
                  onChange={(e) => setResolutionData(prev => ({ ...prev, preventiveMeasures: e?.target?.value }))}
                  placeholder="Measures taken to prevent similar incidents in the future"
                  className="w-full h-20 p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
              </div>

              {/* Lessons Learned */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Lessons Learned
                </label>
                <textarea
                  value={resolutionData?.lessonsLearned}
                  onChange={(e) => setResolutionData(prev => ({ ...prev, lessonsLearned: e?.target?.value }))}
                  placeholder="Key insights and lessons learned from this incident"
                  className="w-full h-20 p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
              </div>

              {/* Follow-up Actions */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Follow-up Actions
                </label>
                <div className="space-y-2 mb-3">
                  {resolutionData?.followUpActions?.map((action) => (
                    <div key={action?.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-foreground">{action?.action}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="X"
                        onClick={() => removeFollowUpAction(action?.id)}
                        className="text-error hover:text-error"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFollowUpAction}
                    onChange={(e) => setNewFollowUpAction(e?.target?.value)}
                    placeholder="Add follow-up action"
                    className="flex-1 p-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyPress={(e) => e?.key === 'Enter' && addFollowUpAction()}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    onClick={addFollowUpAction}
                  />
                </div>
              </div>

              {/* Resources Used */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Resources Used
                </label>
                <div className="space-y-2 mb-3">
                  {resolutionData?.resourcesUsed?.map((resource, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-foreground">{resource}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="X"
                        onClick={() => removeResource(index)}
                        className="text-error hover:text-error"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newResource}
                    onChange={(e) => setNewResource(e?.target?.value)}
                    placeholder="Add resource (tools, documentation, etc.)"
                    className="flex-1 p-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyPress={(e) => e?.key === 'Enter' && addResource()}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    onClick={addResource}
                  />
                </div>
              </div>

              {/* Verification Steps */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Verification Steps
                </label>
                <textarea
                  value={resolutionData?.verificationSteps}
                  onChange={(e) => setResolutionData(prev => ({ ...prev, verificationSteps: e?.target?.value }))}
                  placeholder="Steps taken to verify the resolution was successful"
                  className="w-full h-20 p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
              </div>

              {/* Time to Resolve */}
              <div>
                <Input
                  label="Time to Resolve"
                  type="text"
                  value={resolutionData?.timeToResolve}
                  onChange={(e) => setResolutionData(prev => ({ ...prev, timeToResolve: e?.target?.value }))}
                  placeholder="e.g., 4 hours 30 minutes"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-border">
                <Button
                  variant="default"
                  iconName="Check"
                  onClick={handleSave}
                >
                  Save Resolution
                </Button>
                <Button
                  variant="outline"
                  iconName="X"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Read-only view */}
              <div className="space-y-6">
                {incident?.resolution?.summary && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Resolution Summary</h3>
                    <p className="text-sm text-muted-foreground">{incident?.resolution?.summary}</p>
                  </div>
                )}

                {incident?.resolution?.rootCause && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Root Cause</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{incident?.resolution?.rootCause}</p>
                  </div>
                )}

                {incident?.resolution?.solution && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Solution</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{incident?.resolution?.solution}</p>
                  </div>
                )}

                {incident?.resolution?.preventiveMeasures && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Preventive Measures</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{incident?.resolution?.preventiveMeasures}</p>
                  </div>
                )}

                {incident?.resolution?.lessonsLearned && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Lessons Learned</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{incident?.resolution?.lessonsLearned}</p>
                  </div>
                )}

                {incident?.resolution?.followUpActions && incident?.resolution?.followUpActions?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Follow-up Actions</h3>
                    <div className="space-y-2">
                      {incident?.resolution?.followUpActions?.map((action, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="ArrowRight" size={14} />
                          <span>{action?.action || action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {incident?.resolution?.resourcesUsed && incident?.resolution?.resourcesUsed?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Resources Used</h3>
                    <div className="flex flex-wrap gap-2">
                      {incident?.resolution?.resourcesUsed?.map((resource, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded">
                          {resource}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {incident?.resolution?.verificationSteps && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Verification Steps</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{incident?.resolution?.verificationSteps}</p>
                  </div>
                )}

                {incident?.resolution?.timeToResolve && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">Time to Resolve</h3>
                    <p className="text-sm text-muted-foreground">{incident?.resolution?.timeToResolve}</p>
                  </div>
                )}

                {/* Resolution Metadata */}
                {incident?.resolution && (
                  <div className="pt-4 border-t border-border">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Resolved by:</span>
                        <span className="ml-2 text-foreground font-medium">{incident?.resolution?.resolvedBy}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Resolved at:</span>
                        <span className="ml-2 text-foreground">{new Date(incident.resolution.resolvedAt)?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ResolutionDocumentation;